import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useFindFoldersQuery } from 'client/modules/Surveys/folderApiSlice';
import DefaultCopyField from 'client/util/CopyField';
import generateToken from 'client/util/generateToken';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

function TriggersModal({
  open,
  onClose,
  trigger,
  onSave,
  surveyOptions,
  triggerTypes,
  delayTypes,
  messages,
  notificationToken,
  configRootUrl,
}) {
  const { data: folders = [] } = useFindFoldersQuery();
  const { formatMessage } = useIntl();
  const editing = !!trigger;
  const [form, setForm] = useState({
    survey: trigger?.survey || (surveyOptions[0] && surveyOptions[0].value) || '',
    trigger: trigger?.trigger || triggerTypes[0]?.value || '',
    delay: trigger?.delay || delayTypes[0] || '',
    suffix: trigger?.suffix || '',
  });

  useEffect(() => {
    if (editing) {
      setForm({
        survey: trigger?.survey || (surveyOptions[0] && surveyOptions[0].value) || '',
        trigger: trigger?.trigger || triggerTypes[0]?.value || '',
        delay: trigger?.delay || delayTypes[0] || '',
        suffix: trigger?.suffix || '',
      });
    } else {
      setForm({
        survey: (surveyOptions[0] && surveyOptions[0].value) || '',
        trigger: triggerTypes[0]?.value || '',
        delay: delayTypes[0] || '',
        suffix: generateToken(6),
      });
    }
  }, [delayTypes, editing, open, surveyOptions, trigger, triggerTypes]);

  const handleChange = field => event => {
    setForm(f => ({ ...f, [field]: event.target.value }));
  };

  const handleSave = () => {
    // Include the _id if editing to maintain identity
    const updatedTrigger = editing ? { ...form, _id: trigger._id } : form;
    onSave(updatedTrigger);
    onClose();
  };

  const isCustom = form.trigger === 'Custom';
  const endpointUrl =
    configRootUrl && notificationToken && form.suffix
      ? `${configRootUrl}/api/public/integrations/salesforce/notification/${notificationToken}/${form.suffix}`
      : '';
  const pushApiUrl =
    configRootUrl && notificationToken && form.suffix
      ? `${configRootUrl}/api/public/integrations/salesforce/push/${notificationToken}/${form.suffix}`
      : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editing
          ? formatMessage(messages.edit)
          : formatMessage(messages.addTrigger) || 'Add Trigger'}
      </DialogTitle>
      <DialogContent sx={{ pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.survey)}</InputLabel>
          <Select
            value={form.survey}
            onChange={handleChange('survey')}
            label={formatMessage(messages.survey)}
            required
          >
            {(() => {
              const folderMap = (folders || []).reduce((acc, f) => {
                acc[f._id] = f.name;
                return acc;
              }, {});
              const grouped = {};
              surveyOptions.forEach(opt => {
                const folderId = opt.folder || 'NO_FOLDER';
                if (!grouped[folderId]) grouped[folderId] = [];
                grouped[folderId].push(opt);
              });
              const folderKeys = Object.keys(grouped).sort((a, b) => {
                if (a === 'NO_FOLDER') return -1;
                if (b === 'NO_FOLDER') return 1;
                const nameA = folderMap[a] || 'No Folder';
                const nameB = folderMap[b] || 'No Folder';
                return nameA.localeCompare(nameB);
              });
              return folderKeys.map(folderId => [
                <ListSubheader key={folderId} disableSticky>
                  {folderMap[folderId] ||
                    formatMessage({ id: 'Surveys.NoFolder', defaultMessage: 'No Folder' })}
                </ListSubheader>,
                ...grouped[folderId]
                  .slice()
                  .sort((a, b) => {
                    const nameA = a.name || a.brand || '';
                    const nameB = b.name || b.brand || '';
                    return nameA.localeCompare(nameB);
                  })
                  .map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.name}
                    </MenuItem>
                  )),
              ]);
            })()}
          </Select>
        </FormControl>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.trigger)}</InputLabel>
          <Select
            value={form.trigger}
            onChange={handleChange('trigger')}
            label={formatMessage(messages.trigger)}
            required
          >
            {triggerTypes.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {messages[opt.label] ? formatMessage(messages[opt.label]) : opt.label}
              </MenuItem>
            ))}
          </Select>
          {isCustom && (
            <Typography variant="caption" sx={{ mb: 1 }}>
              <FormattedMessage
                id="IntegrationConfigSalesforce.CustomTrigger"
                defaultMessage={
                  'Establish personalized triggers in Salesforce for specific entity categories such as ' +
                  'Account, Case, Contact, and Opportunity by following the guidelines outlined in our {documentationLink}.'
                }
                values={{
                  documentationLink: (
                    <a
                      href="https://www.promoter.ninja/help/integrations/salesforce-integration"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <FormattedMessage
                        id="IntegrationConfigSalesforce.CustomTriggerDocumentation"
                        defaultMessage="documentation"
                      />
                    </a>
                  ),
                }}
              />
            </Typography>
          )}
        </FormControl>
        <FormControl variant="standard" sx={{ minWidth: 220, mb: 2 }} fullWidth>
          <InputLabel>{formatMessage(messages.delay)}</InputLabel>
          <Select
            value={form.delay}
            onChange={handleChange('delay')}
            label={formatMessage(messages.delay)}
            required
          >
            {delayTypes.map(value => (
              <MenuItem key={value} value={value}>
                {formatMessage(messages[value])}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isCustom && !!endpointUrl && (
          <DefaultCopyField
            label={formatMessage(messages.outboundMessageUrl)}
            variant="standard"
            value={endpointUrl}
          />
        )}
        {!!pushApiUrl && !!form.suffix && (
          <DefaultCopyField
            label={formatMessage(messages.pushApiTriggerUrl)}
            variant="standard"
            value={pushApiUrl}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{formatMessage(messages.cancel) || 'Cancel'}</Button>
        <Button onClick={handleSave} variant="contained">
          {formatMessage(messages.save) || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TriggersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  trigger: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  surveyOptions: PropTypes.array.isRequired,
  triggerTypes: PropTypes.array.isRequired,
  delayTypes: PropTypes.array.isRequired,
  messages: PropTypes.object.isRequired,
  notificationToken: PropTypes.string,
  configRootUrl: PropTypes.string,
};

export default TriggersModal;
